import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC, useRef } from 'react';
import NotificationSelected, { NotificationSelectedHandle } from './notificationSelected';
import { BaseButton } from 'components/BaseButton';
import { TFunction } from 'i18next';
import { Tag } from 'types/auto-templates';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      flexShrink: 0,
      padding: theme.spacing(2, 3, 0),
    },
    title: {
      padding: 0,
    },
    closeButton: {
      flex: '0 0 auto',
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      minHeight: 0,
      minWidth: 0,
      padding: 0,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      flexShrink: 0,
      padding: theme.spacing(2, 3),
      paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
    },
  })
);

type ModalCreateNotificationProps = {
  open: boolean;
  handleClose: () => void;
  handleUpdateCreateNotification: (data: any) => void;
  t: TFunction;
  tagsField: Tag[];
  isSubmitting?: boolean;
}

const emptyDraft = { _id: '', subject: '', html: '', prompt: '' };

const ModalCreateNotification: FC<ModalCreateNotificationProps> = ({ open, handleClose, handleUpdateCreateNotification, t, tagsField, isSubmitting = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const editorRef = useRef<NotificationSelectedHandle>(null);

  return (
    <Dialog
      maxWidth="xl"
      fullWidth
      fullScreen={isFullScreen}
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      PaperProps={{ style: { height: '100%' } }}
      aria-labelledby="create-template-title"
    >
      <Box className={classes.header}>
        <DialogTitle id="create-template-title" className={classes.title}>
          {t('emailTemplates:title-create')}
        </DialogTitle>
        <IconButton
          className={classes.closeButton}
          aria-label={t('common:close')}
          disabled={isSubmitting}
          onClick={handleClose}
          size="large"
        >
          <FontAwesomeIcon icon="times" />
        </IconButton>
      </Box>
      <Box className={classes.content}>
        <NotificationSelected
          ref={editorRef}
          notification={emptyDraft}
          handleUpdateNotification={handleUpdateCreateNotification}
          t={t}
          tagsField={tagsField}
          isModal
          isSubmitting={isSubmitting}
        />
      </Box>
      <Box className={classes.actions}>
        <BaseButton
          buttonVariant="outlined"
          buttonColor="inherit"
          icon="times"
          text={t('common:cancel')}
          ariaLabel={t('common:cancel')}
          disabled={isSubmitting}
          onClick={handleClose}
        />
        <BaseButton
          icon="check"
          text={t('common:save')}
          ariaLabel={t('common:save')}
          isLoading={isSubmitting}
          disabled={isSubmitting}
          onClick={() => editorRef.current?.submit()}
        />
      </Box>
    </Dialog>
  );
};

export default ModalCreateNotification;
